import { roofEvidenceFilterSchema, roofEvidenceSummarySchema } from "./roof-evidence.js";
import { buildingUsageCategoriesSchema, buildingUsageSchema } from "./building-usage.js";
import { Type } from "@sinclair/typebox";
import { aiAreaPreviewSchema, aiTerritorialRunSchema, aiAreaIntentSchema, aiAreaProposalSchema } from "./ai-territory.js";
const object = { additionalProperties: false };
const text = Type.String({ maxLength: 12000 });
const id = Type.String({ format: "uuid" });
const decimal = Type.Union([Type.String({ pattern: "^\\d{1,10}(?:\\.\\d{1,4})?$" }), Type.Null()]);
export const aiResearchCompanySchema = Type.Object({
    name: Type.String({ maxLength: 500 }),
    address: Type.Union([Type.String({ maxLength: 1000 }), Type.Null()]),
    status: Type.Union([Type.String({ maxLength: 100 }), Type.Null()]),
    relation: Type.Union([Type.Literal("business_search"), Type.Literal("cadastral_holder")]),
    activity: Type.Union([Type.String({ maxLength: 1000 }), Type.Null()]),
    employees: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    turnover: Type.Union([Type.Number({ minimum: 0 }), Type.Null()]),
    turnoverYear: Type.Union([Type.Integer({ minimum: 1900, maximum: 2200 }), Type.Null()])
}, object);
const aiStreetResearchImageSchema = Type.Object({
    id: Type.String({ pattern: "^\\d{1,32}$" }),
    capturedAt: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}\\.\\d{3}Z$" }),
    attribution: Type.String({ maxLength: 500 }),
    distanceMeters: Type.Number({ minimum: 0 }),
    sourceUrl: Type.String({ maxLength: 200 }),
    licenseUrl: Type.String({ maxLength: 200 })
}, object);
export const aiAerialResearchImageSchema = Type.Object({
    provider: Type.Literal("mapbox"),
    id,
    capturedAt: Type.Null(),
    acquiredAt: Type.String({ format: "date-time" }),
    attribution: Type.String({ maxLength: 500 }),
    sourceUrl: Type.String({ maxLength: 200 }),
    licenseUrl: Type.String({ maxLength: 200 }),
    geometryCurrent: Type.Boolean()
}, object);
export const aiResearchImageSchema = Type.Union([
    aiStreetResearchImageSchema,
    aiAerialResearchImageSchema
]);
export const aiResearchImagesSchema = Type.Object({ items: Type.Array(aiResearchImageSchema, { maxItems: 8 }) }, object);
export const aiActivitySchema = Type.Object({
    sequence: Type.Integer({ minimum: 1 }),
    phase: Type.Union([
        Type.Literal("area"),
        Type.Literal("buildings"),
        Type.Literal("selection"),
        Type.Literal("building"),
        Type.Literal("sources"),
        Type.Literal("images"),
        Type.Literal("catalog"),
        Type.Literal("web")
    ]),
    state: Type.Union([
        Type.Literal("running"),
        Type.Literal("complete"),
        Type.Literal("empty"),
        Type.Literal("needs_input"),
        Type.Literal("failed")
    ]),
    count: Type.Optional(Type.Integer({ minimum: 0, maximum: 1000 })),
    updatedAt: Type.String()
}, object);
export const aiProgressSchema = Type.Object({
    text: Type.String({ maxLength: 12000 }),
    stage: Type.String({ maxLength: 100 }),
    updatedAt: Type.String(),
    activities: Type.Optional(Type.Array(aiActivitySchema, { maxItems: 24 }))
}, object);
export const aiBriefSchema = Type.Object({
    selectionOrigin: Type.Optional(Type.Union([Type.Literal("operator"), Type.Literal("research")])),
    evidenceSnapshotId: Type.Optional(id),
    territory: Type.String({ maxLength: 300 }),
    municipalityCode: Type.Union([Type.String({ pattern: "^\\d{6}$" }), Type.Null()]),
    areaIntent: Type.Optional(Type.Union([aiAreaIntentSchema, Type.Null()])),
    researchAreaId: Type.Optional(Type.Union([id, Type.Null()])),
    researchAreaRequirement: Type.Optional(Type.Union([
        Type.Literal("industrial"),
        Type.Literal("productive"),
        Type.Literal("named"),
        Type.Literal("operator_defined"),
        Type.Null()
    ])),
    roofEvidence: Type.Optional(roofEvidenceFilterSchema),
    usageCategories: Type.Optional(buildingUsageCategoriesSchema),
    includeEstimatedUsage: Type.Optional(Type.Boolean()),
    buildingType: Type.Union([Type.Literal("industrial"), Type.Literal("any"), Type.Null()]),
    minAreaM2: Type.Union([Type.Number({ minimum: 0, maximum: 10000000 }), Type.Null()]),
    selectionAreaBasis: Type.Optional(Type.Union([
        Type.Literal("footprint"),
        Type.Literal("total_floor"),
        Type.Literal("roof"),
        Type.Literal("unspecified")
    ])),
    maxBuildings: Type.Integer({ minimum: 1, maximum: 20 }),
    works: text,
    constraints: text,
    researchBudgetUsd: Type.Number({ minimum: 0.01, maximum: 1000 }),
    externalBudgetEurCents: Type.Optional(Type.Integer({ minimum: 0, maximum: 10000 })),
    targetPricePerM2: decimal,
    surfaceBasis: Type.Union([
        Type.Literal("footprint"),
        Type.Literal("roof"),
        Type.Literal("unspecified")
    ]),
    vatExcluded: Type.Literal(true),
    structureIds: Type.Optional(Type.Array(id, { maxItems: 20, uniqueItems: true })),
    ambiguities: Type.Array(text, { maxItems: 20 }),
    researchNotes: Type.Optional(Type.Array(text, { maxItems: 20 })),
    clarificationOptions: Type.Optional(Type.Array(Type.String({ maxLength: 300 }), { maxItems: 3 }))
}, object);
export const aiDocumentSchema = Type.Object({
    id,
    name: Type.String({ minLength: 1, maxLength: 240 }),
    role: Type.Union([
        Type.Literal("reference"),
        Type.Literal("adapt_quote"),
        Type.Literal("information")
    ]),
    extraction: Type.Union([
        Type.Literal("pending"),
        Type.Literal("complete"),
        Type.Literal("partial"),
        Type.Literal("failed")
    ]),
    note: text
}, object);
export const aiSourceReferenceSchema = Type.Object({
    documentId: Type.Union([id, Type.Null()]),
    page: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    excerpt: text
}, object);
export const aiFootprintGeometrySchema = Type.Object({
    type: Type.Literal("MultiPolygon"),
    coordinates: Type.Array(Type.Array(Type.Array(Type.Tuple([Type.Number(), Type.Number()]), { maxItems: 10000 }), {
        maxItems: 100
    }), { maxItems: 100 })
}, object);
export const aiMeasurementSchema = Type.Object({
    note: Type.Optional(Type.String({ maxLength: 2000 })),
    mapMeasurementId: Type.Optional(Type.String({ minLength: 1, maxLength: 120 })),
    id: Type.String({ minLength: 1, maxLength: 120 }),
    name: text,
    kind: Type.Union([
        Type.Literal("footprint"),
        Type.Literal("roof_area"),
        Type.Literal("perimeter"),
        Type.Literal("safety_trace"),
        Type.Literal("manual")
    ]),
    value: Type.String({ pattern: "^\\d{1,10}(?:\\.\\d{1,4})?$" }),
    unit: Type.String({ minLength: 1, maxLength: 40 }),
    source: Type.Union([Type.Literal("map"), Type.Literal("document"), Type.Literal("manual")]),
    status: Type.Union([Type.Literal("indicative"), Type.Literal("verified")]),
    reference: aiSourceReferenceSchema,
    geometry: Type.Union([
        aiFootprintGeometrySchema,
        Type.Object({
            type: Type.Union([Type.Literal("LineString"), Type.Literal("Polygon")]),
            coordinates: Type.Array(Type.Tuple([Type.Number(), Type.Number()]), { maxItems: 1000 })
        }, object),
        Type.Null()
    ])
}, object);
export const aiLineSchema = Type.Object({
    id: Type.String({ minLength: 1, maxLength: 120 }),
    name: Type.String({ minLength: 1, maxLength: 300 }),
    description: text,
    unit: Type.String({ minLength: 1, maxLength: 40 }),
    quantity: decimal,
    unitPrice: decimal,
    amount: decimal,
    catalogCode: Type.Union([Type.String({ maxLength: 120 }), Type.Null()]),
    includedInLineId: Type.Optional(Type.Union([Type.String({ minLength: 1, maxLength: 120 }), Type.Null()])),
    costStatus: Type.Optional(Type.Union([Type.Literal("priced"), Type.Literal("unpriced"), Type.Literal("included")])),
    source: text,
    quantityBasis: Type.Union([
        Type.Literal("unknown"),
        Type.Literal("map_estimate"),
        Type.Literal("brief"),
        Type.Literal("manual")
    ]),
    excluded: Type.Boolean(),
    manual: Type.Boolean(),
    measurementId: Type.Optional(Type.String({ maxLength: 120 })),
    sourceReference: Type.Optional(aiSourceReferenceSchema),
    quantityManual: Type.Optional(Type.Boolean()),
    contentManual: Type.Optional(Type.Boolean()),
    priceManual: Type.Optional(Type.Boolean())
}, object);
export const aiAlternativeSchema = Type.Object({
    id: Type.String({ minLength: 1, maxLength: 120 }),
    title: text,
    description: text,
    lines: Type.Array(aiLineSchema, { maxItems: 200 }),
    subtotal: Type.String(),
    incomplete: Type.Boolean(),
    assumptions: Type.Array(text)
}, object);
export const aiDraftSchema = Type.Object({
    revision: Type.Integer({ minimum: 1 }),
    measurements: Type.Optional(Type.Array(aiMeasurementSchema, { maxItems: 100 })),
    title: text,
    brief: aiBriefSchema,
    lines: Type.Array(aiLineSchema, { maxItems: 200 }),
    alternatives: Type.Array(aiAlternativeSchema, { maxItems: 8 }),
    subtotal: Type.String(),
    incomplete: Type.Boolean(),
    assumptions: Type.Array(text),
    limitations: Type.Array(text),
    catalogVersionId: id,
    updatedAt: Type.String(),
    canUndo: Type.Boolean()
}, object);
export const aiMessageSchema = Type.Object({
    id,
    role: Type.Union([Type.Literal("user"), Type.Literal("assistant")]),
    text,
    itemId: Type.Union([id, Type.Null()]),
    createdAt: Type.String()
}, object);
export const aiProposalSchema = Type.Object({
    id,
    baseRevision: Type.Integer(),
    summary: text,
    lines: Type.Array(aiLineSchema, { maxItems: 200 }),
    assumptions: Type.Array(text),
    status: Type.Union([Type.Literal("ready"), Type.Literal("applied"), Type.Literal("dismissed")])
}, object);
export const operationalRoofEvidenceSchema = Type.Object({
    observationId: id,
    snapshotId: id,
    sourceId: text,
    sourceName: text,
    sourceRecordId: text,
    claim: Type.Union([
        Type.Literal("asbestos_documented"),
        Type.Literal("asbestos_suspected"),
        Type.Literal("fibrocement_composition_unknown"),
        Type.Literal("fibrocement_asbestos_free_documented")
    ]),
    association: Type.Literal("operator_location"),
    operatorCorrection: Type.Optional(Type.Object({
        remediationState: Type.Union([Type.Literal("complete"), Type.Null()]),
        note: Type.String(),
        updatedAt: Type.String()
    })),
    limitation: text
}, object);
export const aiItemSchema = Type.Object({
    id,
    structureId: Type.Union([id, Type.Null()]),
    footprintGeometry: Type.Optional(aiFootprintGeometrySchema),
    roofEvidenceSummary: Type.Optional(roofEvidenceSummarySchema),
    operationalRoofEvidence: Type.Optional(Type.Array(operationalRoofEvidenceSchema, { maxItems: 10 })),
    usage: Type.Optional(buildingUsageSchema),
    label: text,
    footprintM2: Type.Union([Type.Number(), Type.Null()]),
    center: Type.Union([Type.Tuple([Type.Number(), Type.Number()]), Type.Null()]),
    state: Type.Union([
        Type.Literal("queued"),
        Type.Literal("running"),
        Type.Literal("ready"),
        Type.Literal("failed"),
        Type.Literal("stopped")
    ]),
    error: Type.Union([text, Type.Null()]),
    draft: Type.Union([aiDraftSchema, Type.Null()]),
    proposals: Type.Array(aiProposalSchema),
    composerRevision: Type.Optional(Type.Integer({ minimum: 1 })),
    workItemId: Type.Union([id, Type.Null()]),
    bundleId: Type.Union([id, Type.Null()])
}, object);
export const aiWorkspaceStateSchema = Type.Union([
    Type.Literal("planning"),
    Type.Literal("clarification"),
    Type.Literal("ready"),
    Type.Literal("running"),
    Type.Literal("completed"),
    Type.Literal("partial"),
    Type.Literal("stopped"),
    Type.Literal("failed"),
    Type.Literal("outcome_uncertain")
]);
export const aiWorkspaceSchema = Type.Object({
    protocolVersion: Type.Optional(Type.Literal(2)),
    briefRevision: Type.Optional(Type.Integer({ minimum: 1 })),
    turnSequence: Type.Optional(Type.Integer({ minimum: 0 })),
    appliedPlanningOperationId: Type.Optional(id),
    parentWorkspaceId: Type.Optional(id),
    dispatchPaused: Type.Optional(Type.Boolean()),
    areaIntentRevision: Type.Optional(Type.Integer({ minimum: 0 })),
    municipalityBounds: Type.Optional(Type.Tuple([
        Type.Number({ minimum: -180, maximum: 180 }),
        Type.Number({ minimum: -90, maximum: 90 }),
        Type.Number({ minimum: -180, maximum: 180 }),
        Type.Number({ minimum: -90, maximum: 90 })
    ])),
    briefProposal: Type.Optional(Type.Object({
        id,
        brief: aiBriefSchema,
        summary: text,
        baseBriefRevision: Type.Integer(),
        areaAction: Type.Optional(Type.Union([Type.Literal("keep"), Type.Literal("replace")])),
        baseTurnSequence: Type.Optional(Type.Integer()),
        appliedWorkspaceId: Type.Union([id, Type.Null()])
    }, object)),
    areaPreview: Type.Optional(aiAreaPreviewSchema),
    manualAreaPreview: Type.Optional(aiAreaPreviewSchema),
    areaProposal: Type.Optional(aiAreaProposalSchema),
    territorialRun: Type.Optional(aiTerritorialRunSchema),
    id,
    title: text,
    revision: Type.Integer(),
    state: aiWorkspaceStateSchema,
    brief: aiBriefSchema,
    frozenBrief: Type.Union([aiBriefSchema, Type.Null()]),
    messages: Type.Array(aiMessageSchema),
    documents: Type.Array(aiDocumentSchema),
    items: Type.Array(aiItemSchema),
    research: Type.Optional(Type.Object({
        area: Type.Union([
            Type.Object({
                label: text,
                attribution: text,
                limitations: Type.Array(text),
                sources: Type.Array(Type.Object({ label: text, url: text }, object))
            }, object),
            Type.Null()
        ]),
        externalReservedCents: Type.Integer({ minimum: 0 }),
        acquisitions: Type.Array(Type.Object({
            structureId: id,
            product: Type.String(),
            state: Type.String(),
            label: Type.String(),
            maximumCents: Type.Integer({ minimum: 0 }),
            actualCostEur: Type.Union([Type.String(), Type.Null()]),
            observedAt: Type.Union([Type.String(), Type.Null()]),
            address: Type.Optional(Type.Union([Type.String({ maxLength: 1000 }), Type.Null()])),
            companies: Type.Optional(Type.Array(aiResearchCompanySchema, { maxItems: 10 }))
        }, object))
    }, object)),
    costBasis: Type.Optional(Type.Union([
        Type.Literal("subscription_estimate"),
        Type.Literal("api_estimate"),
        Type.Literal("unknown")
    ])),
    actualCostUsdMicros: Type.Optional(Type.Union([Type.Integer({ minimum: 0 }), Type.Null()])),
    spentUsdMicros: Type.Integer({ minimum: 0 }),
    reservedUsdMicros: Type.Integer({ minimum: 0 }),
    pendingRequest: Type.Optional(Type.Object({
        operationId: Type.Optional(id),
        kind: Type.String(),
        itemId: Type.Union([id, Type.Null()]),
        state: Type.String(),
        progress: Type.Optional(aiProgressSchema)
    }, object)),
    lastActivity: Type.Optional(Type.Object({
        kind: Type.String(),
        itemId: Type.Union([id, Type.Null()]),
        progress: aiProgressSchema
    }, object)),
    error: Type.Union([text, Type.Null()]),
    createdAt: Type.String(),
    updatedAt: Type.String()
}, object);
export const aiWorkspaceCreateSchema = Type.Object({
    roofEvidence: Type.Optional(roofEvidenceFilterSchema),
    usageCategories: Type.Optional(buildingUsageCategoriesSchema),
    includeEstimatedUsage: Type.Optional(Type.Boolean()),
    idempotencyKey: id,
    message: Type.String({ minLength: 1, maxLength: 12000 }),
    researchBudgetUsd: Type.Number({ minimum: 0.01, maximum: 1000 }),
    externalBudgetEurCents: Type.Optional(Type.Integer({ minimum: 0, maximum: 10000 })),
    structureIds: Type.Optional(Type.Array(id, { maxItems: 20, uniqueItems: true }))
}, object);
export const aiMessageRequestSchema = Type.Object({
    idempotencyKey: id,
    message: Type.String({ minLength: 1, maxLength: 12000 }),
    expectedRevision: Type.Integer({ minimum: 1 }),
    itemId: Type.Optional(id)
}, object);
export const aiBriefUpdateSchema = Type.Object({ expectedRevision: Type.Integer({ minimum: 1 }), brief: aiBriefSchema }, object);
export const aiStartSchema = Type.Object({ expectedRevision: Type.Integer({ minimum: 1 }), idempotencyKey: id }, object);
export const aiDraftUpdateSchema = Type.Object({
    measurements: Type.Optional(Type.Array(aiMeasurementSchema, { maxItems: 100 })),
    expectedRevision: Type.Integer({ minimum: 1 }),
    title: text,
    brief: aiBriefSchema,
    lines: Type.Array(aiLineSchema, { maxItems: 200 })
}, object);
export const aiApplySchema = Type.Object({
    expectedRevision: Type.Integer({ minimum: 1 }),
    proposalId: Type.Optional(id),
    alternativeId: Type.Optional(Type.String({ maxLength: 120 })),
    replaceManual: Type.Optional(Type.Boolean()),
    replaceFields: Type.Optional(Type.Array(Type.Object({
        lineId: Type.String({ minLength: 1, maxLength: 120 }),
        fields: Type.Array(Type.Union([Type.Literal("quantity"), Type.Literal("unitPrice")]), {
            minItems: 1,
            maxItems: 2,
            uniqueItems: true
        })
    }, object), { maxItems: 200 }))
}, object);
export const aiDocumentUploadSchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 240 }),
    role: Type.Union([
        Type.Literal("reference"),
        Type.Literal("adapt_quote"),
        Type.Literal("information")
    ]),
    data: Type.String({ minLength: 8, maxLength: 14000000 }),
    expectedRevision: Type.Integer({ minimum: 1 })
}, object);
export const aiAvailabilitySchema = Type.Object({
    available: Type.Boolean(),
    transport: Type.Optional(Type.Union([Type.Literal("codex"), Type.Literal("openai")])),
    billingBasis: Type.Optional(Type.Union([Type.Literal("subscription_estimate"), Type.Literal("api_estimate")])),
    reason: Type.Union([text, Type.Null()])
}, object);
//# sourceMappingURL=ai-workspace.js.map